import React, { useEffect, useMemo } from "react";
import { NavigationControl } from "react-map-gl/maplibre";
import { useDispatch, useSelector } from "react-redux";

import { MapDeckGl } from "@nens/deck-gl";

import { AppDispatch } from "../App";
import { getMap, getSetView, setViewLocationAndZoom } from "../state/map";
import { selectAsset } from "../state/selectedAsset";
import { getInitialViewstate, getObjects, getObjectsPerType } from "../state/simulation";
import { Asset } from "../types/assets";

import { MAP_TILER_API_KEY } from "./DeckGl/DeckGlConsts";
import DeckGlEventsHandler from "./DeckGl/events/DeckGlEventsHandler";
import MapLibreEventsHandler from "./DeckGl/events/MapLibreEventHandlers";
import useTerrainCanvasInfo from "./DeckGl/terrainLayer/useTerrainCanvasInfo";
import TwoDFlow from "./DeckGl/TwoDFlow/TwoDFlow";
import useBackgroundLayer from "./DeckGl/useBackgroundLayer";
import useDischargeOrPumpLayer from "./DeckGl/useDischargeOrPumpLayer";
import useGridLinesLayer from "./DeckGl/useGridLinesLayer";
import useLeveeEditTool from "./DeckGl/useLeveeEditTool";
import useLineLayer from "./DeckGl/useLineLayer";
import useMapHoverCircle from "./DeckGl/useMapHoverCircle";
import useNodeLayer from "./DeckGl/useNodeLayer";
import useProfileLineTool from "./DeckGl/useProfileLineTool";
import usePumpArrowLayer from "./DeckGl/usePumpArrowLayer";
import useRasterEditTool from "./DeckGl/useRasterEditTool";
import useSelectedPointLayer from "./DeckGl/useSelectedPointLayer";
import useTerrainLayer from "./DeckGl/useTerrainLayer";
import useWaterDepthImageLayer from "./DeckGl/useWaterDepthImageLayer";
import useWaterflowLayer from "./DeckGl/useWaterflowLayer";
import SearchBox from "./SearchBox";
import "./DeckGlLayers.css";

const DeckGlLayer: React.FC = () => {
  const dispatch = useDispatch() as AppDispatch;
  const initialViewState = useSelector(getInitialViewstate);
  const setSelectedAsset = (id: string) => dispatch(selectAsset(id));
  const objectsPerType = useSelector(getObjectsPerType);
  const objects = useSelector(getObjects);

  const assets = useMemo(() => {
    const returnAssets: Record<string, Asset[]> = {};
    const objectsPerTypeKeys = Object.keys(objectsPerType);
    if (!objectsPerTypeKeys.length) return undefined;
    Object.keys(objectsPerType).forEach((key) => {
      const assetsForType = objectsPerType[key].map((value) => objects[value]);
      returnAssets[key] = assetsForType;
    });
    return returnAssets;
  }, [objects, objectsPerType]);

  const waterflowLayer = useWaterflowLayer(assets);
  const leveeEditTool = useLeveeEditTool();
  const pumpLayer = useDischargeOrPumpLayer("PUMP");
  const dischargeLayer = useDischargeOrPumpLayer("DISCHARGE");
  const selectedPointLayer = useSelectedPointLayer();
  const profileLineTool = useProfileLineTool();
  const rasterEditTool = useRasterEditTool();
  const mapHoverCircle = useMapHoverCircle();
  const lineLayer = useLineLayer(setSelectedAsset, assets);
  const gridLineLayer = useGridLinesLayer();
  const backgroundLayer = useBackgroundLayer();
  const terrainCanvasInfo = useTerrainCanvasInfo();
  const terrainLayer = useTerrainLayer(terrainCanvasInfo);
  const pumpArrowLayer = usePumpArrowLayer(assets);

  const waterDepthImage = useWaterDepthImageLayer();
  const nodeLayer = useNodeLayer(setSelectedAsset, assets);

  const setViewValue = useSelector(getSetView);
  const map = useSelector(getMap);

  useEffect(() => {
    const { location, zoom } = setViewValue;
    if (!map || !location || !zoom) return;
    map.flyTo({
      center: location,
      zoom: zoom,
      bearing: 0,
      curve: 1,
      easing(t) {
        return t;
      },
      essential: true,
    });
    dispatch(setViewLocationAndZoom(null, 0));
  }, [dispatch, map, setViewValue]);

  if (!initialViewState) return <></>;

  return (
    <MapDeckGl
      mapProps={{
        // Street, country etc labels
        mapStyle: `https://api.maptiler.com/maps/ec479fb9-0511-4946-8089-c75f12503486/style.json?key=${MAP_TILER_API_KEY}`,
        initialViewState: initialViewState,

        ...MapLibreEventsHandler(),
      }}
      deckGlProps={{
        layers: [
          backgroundLayer,
          terrainLayer,
          waterDepthImage,
          lineLayer,
          pumpArrowLayer,
          gridLineLayer,
          waterflowLayer,
          nodeLayer,
          pumpLayer,
          dischargeLayer,
          selectedPointLayer,
          mapHoverCircle,
          leveeEditTool,
          profileLineTool,
          rasterEditTool,
        ],
        pickingRadius: 5,
        useDevicePixels: false,

        ...DeckGlEventsHandler(),
      }}
    >
      <SearchBox />
      <TwoDFlow terrainCanvasInfo={terrainCanvasInfo} />
      <NavigationControl position="bottom-right" />
    </MapDeckGl>
  );
};

export default DeckGlLayer;
